/**
 * retrieves pagination data from the response headers.
 * @param {Object} headers - the HTTP response headers.
 * @returns {Object} object containing pagination data.
 */
export const getPaginateData = (headers) => {
  const page = Number(headers["x-page"]);
  const total = Number(headers["x-total"]);
  const perPage = Number(headers["x-per-page"]);
  const pages = Math.ceil(total / perPage);

  return {
    page: page,
    pages: pages,
    total: total,
    perPage: perPage,
  };
};

/**
 * Extracts IDs from multiple specified filters in a query parameter object.
 * If a filter value is enclosed in square brackets (e.g., "[123]"),
 * it extracts the ID inside and updates the object.
 *
 * @param {Object} params - The object containing query parameters.
 * @param {string[]} filterNames - An array of keys to process within `params`.
 * @returns {Object} A new object where each specified filter's value is replaced by the extracted ID.
 * If a filter is missing or incorrectly formatted, its original value remains unchanged.
 */
export const obteinIdInFormatedStringQuery = (params, filterNames) => {
  return filterNames.reduce(
    (acc, filterName) => {
      if (acc[filterName]) {
        const formatedId = obtainId(acc[filterName]);
        return { ...acc, [filterName]: formatedId };
      }
      return acc;
    },
    { ...params }
  );
};

/**
 * Extracts the content inside square brackets from a string.
 * If the string is not correctly formatted, it returns the original string.
 *
 * @param {string} string - The string to extract the ID from.
 * @returns {string} The extracted ID or the original string if not formatted correctly.
 */
export const obtainId = (string) => {
  const start = string.indexOf("[");
  const end = string.indexOf("]");

  if (start === -1 || end === -1 || start > end) return string;

  return string.substring(start + 1, end);
};

/**
 * Validates and normalizes a tab value by ensuring it is within an accepted list.
 * If the provided tab is not in the list, it defaults to "All".
 *
 * @param {string} tab - The tab value to validate.
 * @param {string[]} acceptedValues - An array of allowed tab values.
 * @returns {string} The validated tab value if it exists in `acceptedValues`, otherwise "All".
 */
export const parseTab = (tab, acceptedValues) => {
  return acceptedValues.includes(tab) ? tab : "All";
};

/**
 * Sums the numeric values of specific keys within the `stats` object.
 *
 * @param {Object} stats - Object containing statistics with key-value pairs (values should be numeric).
 * @param {Array<string>} [keys] - Optional array of keys to include in the sum.
 *                                 If not provided, all values in the object will be summed.
 * @returns {number} - The sum of the selected values, or -1 if `stats` is not a valid object.
 */
export const sumTabsStats = (stats, keys) => {
  let s = 0;

  if (!stats) return -1;

  Object.keys(stats).forEach((key) => {
    if (!keys || keys.includes(key)) s += stats[key] * 1;
  });

  return s;
};

/**
 * Returns a formatted list of column definitions for a table.
 *
 * Filters out columns that should be hidden based on the provided data,
 * then maps each visible column to its corresponding definition in ALL_COLUMNS,
 * while preserving size and horizontal alignment.
 *
 * @param {Object} ALL_COLUMNS - A dictionary of all available column definitions.
 * @param {Array} columns - The array of column config objects (each may include `name`, `size`, `hAlign`, and optional `hide` function).
 * @param {any} data - The data element used to determine whether a column should be hidden.
 * @returns {Array} - An array of column definitions ready for rendering in a table.
 */
export const getColumnsForTable = (ALL_COLUMNS, columns, data) => {
  return columns
    .filter((column) => !(column.hide && column.hide(data)))
    .map((column) => ({
      ...ALL_COLUMNS[column.name],
      size: column.size,
      hAlign: column.hAlign,
    }));
};
